import clsx from 'clsx';
import {
  ProportionChart,
  ProportionChartBar,
  ProportionChartListItem,
} from '../../UI';
import { displayTerm } from '../../util';
import {
  LIQUID_ASSET_SUMMARY_PROPORTION_CHART_AFTER_TAX_INVESTMENTS_COLOR,
  LIQUID_ASSET_SUMMARY_PROPORTION_CHART_BUSINESS_CASH_COLOR,
  LIQUID_ASSET_SUMMARY_PROPORTION_CHART_PERSONAL_CASH_COLOR,
} from '../constants';
import {
  UseLiquidAssetSummaryProportionChartArgs,
  useLiquidAssetSummaryProportionChart,
} from '../hooks';

interface LiquidAssetSummaryProportionChart
  extends UseLiquidAssetSummaryProportionChartArgs {
  className?: string;
}

export function LiquidAssetSummaryProportionChart({
  className,
  ...useLiquidAssetSummaryProportionChartArgs
}: LiquidAssetSummaryProportionChart) {
  const { isLoading, ltDetails, tUI } = useLiquidAssetSummaryProportionChart(
    useLiquidAssetSummaryProportionChartArgs
  );
  const annualSpending = ltDetails?.annualExpenses?.value;
  const liquidAssetRows = [
    {
      id: 'totalPersonalCash',
      amount: ltDetails?.totalPersonalCash,
      color: LIQUID_ASSET_SUMMARY_PROPORTION_CHART_PERSONAL_CASH_COLOR,
      title: tUI('liquid-asset-summary-proportion-chart-personal-cash'),
    },
    {
      id: 'totalBusinessCash',
      amount: ltDetails?.totalBusinessCash,
      color: LIQUID_ASSET_SUMMARY_PROPORTION_CHART_BUSINESS_CASH_COLOR,
      title: tUI('liquid-asset-summary-proportion-chart-business-cash'),
    },
    {
      id: 'totalAfterTaxInvestments',
      amount: ltDetails?.totalAfterTaxInvestments,
      color: LIQUID_ASSET_SUMMARY_PROPORTION_CHART_AFTER_TAX_INVESTMENTS_COLOR,
      title: tUI('liquid-asset-summary-proportion-chart-after-tax-investments'),
    },
  ]
    .filter(({ amount }) => isLoading || (amount?.value ?? 0) !== 0)
    .map((row) => {
      const balance = row.amount?.value;
      const contribution =
        balance == null || annualSpending == null
          ? undefined
          : balance <= 0 || annualSpending <= 0
            ? 0
            : balance / annualSpending;

      return { ...row, contribution };
    });
  const hasCalculatedContribution = liquidAssetRows.some(({ contribution }) => contribution !== undefined);
  const hasNegativeBalance = liquidAssetRows.some(({ amount }) => (amount?.value ?? 0) < 0);

  return (
    <ProportionChart
      className={clsx(
        className,
        'LiquidAssetSummaryProportionChart',
        '[&_.ProportionChart-TotalTitle]:!mix-blend-difference',
        'md:[&_.ProportionChart-TotalContainer]:!pr-32'
      )}
      totalAmount={ltDetails?.totalLiquidAssets}
      title={tUI('liquid-asset-summary-proportion-chart-title')}
      totalTitle={tUI('liquid-asset-summary-proportion-chart-total-title')}
      disclaimer={
        hasCalculatedContribution
          ? tUI(
              hasNegativeBalance
                ? 'liquid-asset-summary-proportion-chart-negative-balance-disclosure'
                : 'liquid-asset-summary-proportion-chart-contribution-rounding-disclosure'
            )
          : undefined
      }
      tableHeader={
        liquidAssetRows.length > 0 ? (
          <div className="LiquidAssetSummaryProportionChart-Header flex h-7 items-end justify-end px-4 pb-1 text-[11px] font-medium leading-none text-darkMode-gray-medium dark:text-darkMode-gray-light/70">
            <span className="w-[129px] text-right">{tUI('liquid-asset-summary-proportion-chart-balance-column')}</span>
            <span className="ml-4 hidden w-24 shrink-0 text-right md:block">
              {tUI('liquid-asset-summary-proportion-chart-contribution-column')}
            </span>
          </div>
        ) : undefined
      }
      barChartComponent={
        <ProportionChartBar
          data={liquidAssetRows.map(({ id, amount, color }) => ({
            id,
            value: amount,
            color,
          }))}
        />
      }
    >
      {liquidAssetRows.map(({ id, amount, color, title, contribution }) => {
        const contributionDisplay = displayTerm(contribution);

        return (
          <ProportionChartListItem
            key={id}
            amount={amount}
            color={color}
            title={title}
            amountSubtitle={
              <span className="MonetaryListItem-amount-subtitle mt-0.5 text-right text-[11px] text-darkMode-gray-medium dark:text-darkMode-gray-light/70 md:hidden">
                {tUI('liquid-asset-summary-proportion-chart-contribution-column')}: {contributionDisplay}
              </span>
            }
            contentSuffix={
              <span className="LiquidAssetSummaryProportionChart-Contribution ml-4 hidden w-24 shrink-0 self-center text-right text-lg font-bold leading-6 tracking-[-0.39px] text-darkMode-gray-dark dark:text-white md:block">
                {contributionDisplay}
              </span>
            }
          />
        );
      })}
    </ProportionChart>
  );
}
